/**
 * Live multi-step flow state for OS/hardware back parity with in-app ←.
 *
 * Single publisher SoT for system-back in-flow hints:
 * - `publishFlow(input)` — imperative writer (checkout / donation screens)
 * - `usePublishHierarchicalBackFlow(input)` — mount-scoped wrapper around publishFlow/clearFlow
 * - `consumeStepBack()` — same-URL in-flow pop (runs registered `onStepBack`;
 *   when `backDisabled`, returns true without invoking the handler so POP restoreLeft
 *   keeps the URL and does not advance the checkout/donation step)
 * - `stepBackTo` — cross-route parent when consumeStepBack cannot stay on the URL
 *
 * JourneyContext does not mirror these fields.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useOptionalJourneyContext } from './JourneyContext.js';

export type HierarchicalBackFlowKind = 'checkout' | 'donation' | null;

export type HierarchicalBackPaymentRailUiStep = 'primary' | 'gateway-methods' | null;

export interface HierarchicalBackFlowSnapshot {
  readonly flowKind: HierarchicalBackFlowKind;
  readonly flowStep: string | null;
  readonly paymentRailUiStep: HierarchicalBackPaymentRailUiStep;
  readonly paymentActive: boolean;
  /** Named parent (or same checkout URL for same-URL step pops). Null when none. */
  readonly stepBackTo: string | null;
  /** True when a same-URL `onStepBack` handler is registered (shell ← / POP consume). */
  readonly hasOnStepBack: boolean;
  /**
   * When true, `consumeStepBack` returns true without invoking `onStepBack`
   * (blocks POP restoreLeft step advance while chrome ← is disabled).
   */
  readonly backDisabled: boolean;
}

/**
 * Publisher input (checkout / donation screens).
 * `onStepBack` — same-URL / in-flow pop; omit or null when only `stepBackTo` applies.
 * `backDisabled` — default false; when true, consumeStepBack is a no-op that still returns true.
 */
export interface HierarchicalBackFlowPublishInput {
  readonly flowKind: Exclude<HierarchicalBackFlowKind, null>;
  readonly flowStep: string;
  readonly paymentRailUiStep?: HierarchicalBackPaymentRailUiStep;
  readonly paymentActive?: boolean;
  readonly stepBackTo: string | null;
  readonly onStepBack?: (() => void) | null;
  readonly backDisabled?: boolean;
}

/** @deprecated Prefer HierarchicalBackFlowPublishInput — kept as alias. */
export type HierarchicalBackFlowPublish = HierarchicalBackFlowPublishInput & {
  /** @deprecated Use onStepBack. Boolean-returning variant still honored by publishFlow. */
  readonly onPopStep?: (() => boolean) | null;
};

const EMPTY_SNAPSHOT: HierarchicalBackFlowSnapshot = {
  flowKind: null,
  flowStep: null,
  paymentRailUiStep: null,
  paymentActive: false,
  stepBackTo: null,
  hasOnStepBack: false,
  backDisabled: false,
};

export interface HierarchicalBackFlowValue extends HierarchicalBackFlowSnapshot {
  /** Sole writer for system-back in-flow state. */
  readonly publishFlow: (next: HierarchicalBackFlowPublish) => void;
  readonly clearFlow: () => void;
  /**
   * Run the registered onStepBack. Returns true when a same-URL handler is registered:
   * invokes it unless `backDisabled` (no-op — POP caller must restoreLeft / stay on URL).
   * Returns false when no handler (fall through to stepBackTo / hierarchical).
   */
  readonly consumeStepBack: () => boolean;
}

/** Alias used by W7/W8 tests — same as HierarchicalBackFlowValue. */
export type HierarchicalBackFlowContextValue = HierarchicalBackFlowValue;

const HierarchicalBackFlowContext = createContext<HierarchicalBackFlowValue | null>(null);

interface HierarchicalBackFlowProviderProps {
  readonly children: ReactNode;
}

export function HierarchicalBackFlowProvider({
  children,
}: HierarchicalBackFlowProviderProps): JSX.Element {
  const [snapshot, setSnapshot] = useState<HierarchicalBackFlowSnapshot>(EMPTY_SNAPSHOT);
  const onStepBackRef = useRef<(() => void) | null>(null);
  const backDisabledRef = useRef(false);

  const clearGenerationRef = useRef(0);

  const publishFlow = useCallback((next: HierarchicalBackFlowPublish): void => {
    clearGenerationRef.current += 1;
    const { onStepBack, onPopStep, ...rest } = next;
    if (onPopStep !== undefined && onPopStep !== null) {
      onStepBackRef.current = (): void => {
        onPopStep();
      };
    } else {
      onStepBackRef.current = onStepBack ?? null;
    }
    const paymentRailUiStep = rest.paymentRailUiStep ?? null;
    const paymentActive = rest.paymentActive ?? false;
    const backDisabled = rest.backDisabled ?? false;
    backDisabledRef.current = backDisabled;
    const hasOnStepBack = onStepBackRef.current !== null;
    setSnapshot((previous) => {
      if (
        previous.flowKind === rest.flowKind &&
        previous.flowStep === rest.flowStep &&
        previous.paymentRailUiStep === paymentRailUiStep &&
        previous.paymentActive === paymentActive &&
        previous.stepBackTo === rest.stepBackTo &&
        previous.hasOnStepBack === hasOnStepBack &&
        previous.backDisabled === backDisabled
      ) {
        return previous;
      }
      return {
        flowKind: rest.flowKind,
        flowStep: rest.flowStep,
        paymentRailUiStep,
        paymentActive,
        stepBackTo: rest.stepBackTo,
        hasOnStepBack,
        backDisabled,
      };
    });
  }, []);

  const clearFlow = useCallback((): void => {
    setSnapshot(EMPTY_SNAPSHOT);
    // MOB-05: checkout unmounts on SPA POP before shell can consumeStepBack.
    // Keep handler until the next microtask so useLayoutEffect POP can run;
    // publishFlow bumps clearGenerationRef and cancels this deferred clear.
    const generation = clearGenerationRef.current + 1;
    clearGenerationRef.current = generation;
    queueMicrotask(() => {
      if (clearGenerationRef.current !== generation) {
        return;
      }
      onStepBackRef.current = null;
      backDisabledRef.current = false;
    });
  }, []);

  const consumeStepBack = useCallback((): boolean => {
    const handler = onStepBackRef.current;
    if (handler === null) {
      return false;
    }
    // Locked chrome ← / payActionsDisabled: claim same-URL POP without stepping.
    if (backDisabledRef.current) {
      return true;
    }
    handler();
    return true;
  }, []);

  const value = useMemo<HierarchicalBackFlowValue>(
    () => ({
      ...snapshot,
      publishFlow,
      clearFlow,
      consumeStepBack,
    }),
    [snapshot, publishFlow, clearFlow, consumeStepBack],
  );

  return (
    <HierarchicalBackFlowContext.Provider value={value}>
      {children}
    </HierarchicalBackFlowContext.Provider>
  );
}

export function useHierarchicalBackFlow(): HierarchicalBackFlowValue {
  const ctx = useContext(HierarchicalBackFlowContext);
  if (ctx === null) {
    throw new Error('useHierarchicalBackFlow must be used inside <HierarchicalBackFlowProvider>');
  }
  return ctx;
}

/** Safe for system-back / chrome outside the provider. */
export function useOptionalHierarchicalBackFlow(): HierarchicalBackFlowValue | null {
  return useContext(HierarchicalBackFlowContext);
}

/**
 * Single React publisher: write HierarchicalBackFlow while mounted; clear on unmount
 * or when input is null. Also mirrors `paymentActive` onto Journey RouteContext.
 * Do not call Journey setSystemBackHints — that API was removed.
 */
export function usePublishHierarchicalBackFlow(
  input: HierarchicalBackFlowPublishInput | null,
): void {
  const backFlow = useOptionalHierarchicalBackFlow();
  const journeyCtx = useOptionalJourneyContext();
  const publishFlow = backFlow?.publishFlow;
  const clearFlow = backFlow?.clearFlow;
  const updateRouteContext = journeyCtx?.updateRouteContext;
  const onStepBack = input?.onStepBack ?? null;
  const onStepBackRef = useRef(onStepBack);
  useEffect(() => {
    onStepBackRef.current = onStepBack;
  }, [onStepBack]);

  const flowKind = input?.flowKind ?? null;
  const flowStep = input?.flowStep ?? null;
  const paymentRailUiStep = input?.paymentRailUiStep ?? null;
  const paymentActive = input?.paymentActive ?? false;
  const stepBackTo = input?.stepBackTo ?? null;
  const backDisabled = input?.backDisabled ?? false;
  const hasInput = input !== null;

  useEffect(() => {
    if (!hasInput || flowKind === null || flowStep === null) {
      clearFlow?.();
      updateRouteContext?.({ paymentActive: false });
      return;
    }
    updateRouteContext?.({ paymentActive });
    publishFlow?.({
      flowKind,
      flowStep,
      paymentRailUiStep,
      paymentActive,
      stepBackTo,
      backDisabled,
      onStepBack: onStepBackRef.current === null
        ? null
        : (): void => {
            onStepBackRef.current?.();
          },
    });
    return () => {
      clearFlow?.();
      updateRouteContext?.({ paymentActive: false });
    };
  }, [
    backDisabled,
    clearFlow,
    flowKind,
    flowStep,
    hasInput,
    paymentActive,
    paymentRailUiStep,
    publishFlow,
    stepBackTo,
    updateRouteContext,
  ]);
}
